import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createTenant, migrateTenant, seedTenant } from '../../../api/tenants.api';
import { fetchActivePlans } from '../../../api/plans.api';
import { buildTenantLoginUrl } from '../../../config/tenantPortal.config';
import type { CreateTenantPayload } from '../../../types/tenant.types';
import type { AdminPlan } from '../../../types/plan.types';
import CustomPlanFields, {
  CUSTOM_PLAN_VALUE,
  customPlanFormToPayload,
  emptyCustomPlanForm,
  type CustomPlanFormState,
} from './CustomPlanFields';

const inputCls =
  'w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 bg-white';

export type CreatedTenantResult = {
  email: string;
  password: string;
  tenantName: string;
  loginUrl: string;
  tenantId?: string | number | null;
};

interface CreateTenantModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (result: CreatedTenantResult) => void;
}

type CreateTenantForm = {
  name: string;
  email: string;
  password: string;
  phone: string;
  plan_id: string;
};

const defaultForm: CreateTenantForm = {
  name: '',
  email: '',
  password: '',
  phone: '',
  plan_id: '',
};

export default function CreateTenantModal({ open, onClose, onCreated }: CreateTenantModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState<CreateTenantForm>(defaultForm);
  const [customPlan, setCustomPlan] = useState<CustomPlanFormState>(emptyCustomPlanForm());
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    void fetchActivePlans().then((result) => {
      if (result.ok) {
        setPlans(result.plans);
        if (!form.plan_id && result.plans[0]) {
          setForm((prev) => ({ ...prev, plan_id: String(result.plans[0].id) }));
        }
      }
    });
  }, [open]);

  if (!open) return null;

  const set = <K extends keyof CreateTenantForm>(key: K, val: CreateTenantForm[K]) => {
    setForm((p) => ({ ...p, [key]: val }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const tenantName = form.name;
    const isCustom = form.plan_id === CUSTOM_PLAN_VALUE;
    if (!form.plan_id) {
      setError(t('ateliers.create.plan_required'));
      return;
    }
    if (isCustom && (!customPlan.monthly_price || !customPlan.yearly_price || !customPlan.starts_at)) {
      setError(t('ateliers.custom_plan.required'));
      return;
    }
    const apiPayload: CreateTenantPayload = isCustom
      ? {
          name: form.name,
          custom_plan: true,
          custom_subscription: customPlanFormToPayload(customPlan),
        }
      : {
          name: form.name,
          plan_id: Number(form.plan_id),
        };
    setLoading(true);
    const created = await createTenant(apiPayload);
    if (created.ok === false) {
      setLoading(false);
      if (created.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(created.message);
      return;
    }

    if (!created.tenantId) {
      setLoading(false);
      setError(t('ateliers.create.provisioning_missing_tenant_id'));
      return;
    }

    const migrated = await migrateTenant(created.tenantId);
    if (migrated.ok === false) {
      setLoading(false);
      if (migrated.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(migrated.message);
      return;
    }

    const seeded = await seedTenant(created.tenantId, {
      email: form.email,
      password: form.password,
      admin_email: form.email,
      admin_password: form.password,
      phone: form.phone || undefined,
    });
    setLoading(false);
    if (seeded.ok === false) {
      if (seeded.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(seeded.message);
      return;
    }
    const loginEmail = seeded.credentials?.email || created.credentials?.email || form.email;
    const loginPassword =
      seeded.credentials?.password || created.credentials?.password || form.password;
    const loginUrl = buildTenantLoginUrl();
    setForm(defaultForm);
    setCustomPlan(emptyCustomPlanForm());
    onCreated({
      email: loginEmail,
      password: loginPassword,
      tenantName,
      loginUrl,
      tenantId: created.tenantId,
    });
    onClose();
  };

  const handleClose = () => {
    if (!loading) {
      setForm(defaultForm);
      setCustomPlan(emptyCustomPlanForm());
      setError('');
      onClose();
    }
  };

  const fields: {
    key: keyof CreateTenantForm;
    type: string;
    auto: string;
    required?: boolean;
  }[] = [
    { key: 'name', type: 'text', auto: 'organization', required: true },
    { key: 'email', type: 'email', auto: 'email', required: true },
    { key: 'password', type: 'password', auto: 'new-password', required: true },
    { key: 'phone', type: 'tel', auto: 'tel', required: false },
  ];

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-base font-bold text-gray-900">{t('ateliers.create.title')}</h3>
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer disabled:opacity-50"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="rounded-xl bg-teal-50 border border-teal-100 px-3 py-2.5 text-xs text-teal-900 leading-relaxed">
            {t('ateliers.create.prefer_order_plans')}{' '}
            <button
              type="button"
              className="font-semibold underline underline-offset-2"
              onClick={() => {
                onClose();
                navigate('/order-plans');
              }}
            >
              {t('ateliers.create.go_order_plans')}
            </button>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              {t('ateliers.create.fields.plan')}
            </label>
            <select
              value={form.plan_id}
              onChange={(e) => set('plan_id', e.target.value)}
              disabled={loading}
              required
              className={inputCls}
            >
              {plans.length === 0 ? (
                <option value="">{t('ateliers.create.loading_plans')}</option>
              ) : null}
              {plans.map((plan) => (
                <option key={plan.id} value={String(plan.id)}>
                  {plan.title || plan.name} — ${plan.price}
                </option>
              ))}
              <option value={CUSTOM_PLAN_VALUE}>{t('ateliers.custom_plan.option')}</option>
            </select>
          </div>

          {form.plan_id === CUSTOM_PLAN_VALUE ? (
            <CustomPlanFields value={customPlan} onChange={setCustomPlan} disabled={loading} />
          ) : null}

          {fields.map((field) => (
            <div key={field.key}>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                {t(`ateliers.create.fields.${field.key}`)}
              </label>
              <input
                type={field.type}
                value={form[field.key]}
                onChange={(e) => set(field.key, e.target.value)}
                autoComplete={field.auto}
                disabled={loading}
                required={field.required !== false}
                className={inputCls}
              />
            </div>
          ))}

          {error && (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-rose-50 rounded-lg border border-rose-100 text-sm text-rose-600">
              <i className="ri-error-warning-line flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl cursor-pointer disabled:opacity-50"
            >
              {t('ateliers.create.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 bg-teal-600 text-white text-sm font-semibold rounded-xl hover:bg-teal-700 cursor-pointer disabled:opacity-60"
            >
              {loading ? t('ateliers.create.submitting') : t('ateliers.create.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
